import {
  Building2,
  Crown,
  Mail,
  Settings,
  Shield,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { bugsnagClient } from "../bugsnag";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";
import api from "../utils/api";

const OrganizationManagement = () => {
  const { t } = useTranslation();

  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [stats, setStats] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "member" });
  const [settingsForm, setSettingsForm] = useState({});

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      const [orgData, membersData, statsData] = await Promise.all([
        api.getMyOrganization(),
        api.getOrganizationMembers(),
        api.getOrganizationStats(),
      ]);

      setOrganization(orgData.organization);
      setUserRole(orgData.userRole);
      setMembers(membersData.members);
      setInvitations(membersData.invitations);
      setStats(statsData);
      setSettingsForm({
        name: orgData.organization.name,
        description: orgData.organization.description || "",
      });
    } catch (err) {
      setError(err.message);
      bugsnagClient.notify(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      await api.inviteUserToOrganization(inviteForm);
      trackEvent(ANALYTICS_EVENTS.TEAM_MEMBER_INVITED, {
        role: inviteForm.role,
      });
      setShowInviteModal(false);
      setInviteForm({ email: "", role: "member" });
      await fetchOrganizationData();
      alert(t("organization_management.alerts.invite_success"));
    } catch (err) {
      bugsnagClient.notify(err);
      alert(err.message || t("organization_management.alerts.invite_failure"));
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      await api.updateOrganization(settingsForm);
      trackEvent(ANALYTICS_EVENTS.ORGANIZATION_UPDATED);
      setShowSettingsModal(false);
      await fetchOrganizationData();
      alert(t("organization_management.alerts.settings_success"));
    } catch (err) {
      bugsnagClient.notify(err);
      alert(
        err.message || t("organization_management.alerts.settings_failure"),
      );
    }
  };

  const handleUpdateMemberRole = async (userId, newRole) => {
    try {
      await api.updateMemberRole(userId, newRole);
      trackEvent(ANALYTICS_EVENTS.MEMBER_ROLE_UPDATED, { newRole });
      await fetchOrganizationData();
      alert(t("organization_management.alerts.role_success"));
    } catch (err) {
      bugsnagClient.notify(err);
      alert(err.message || t("organization_management.alerts.role_failure"));
    }
  };

  const handleRemoveMember = async (userId) => {
    if (window.confirm(t("organization_management.alerts.remove_confirm"))) {
      try {
        await api.removeMember(userId);
        trackEvent(ANALYTICS_EVENTS.TEAM_MEMBER_REMOVED);
        await fetchOrganizationData();
        alert(t("organization_management.alerts.remove_success"));
      } catch (err) {
        bugsnagClient.notify(err);
        alert(
          err.message || t("organization_management.alerts.remove_failure"),
        );
      }
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "manager":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const canManageMembers = userRole === "admin";
  const canManageSettings = userRole === "admin";

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">
            {t("organization_management.error_loading")}
          </h3>
          <p className="text-red-600 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Building2 className="mr-3" />
              {organization?.name}
            </h1>
            <p className="text-gray-600 mt-1">{organization?.description}</p>
            <div className="flex items-center mt-2">
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(
                  userRole,
                )}`}
              >
                {userRole
                  ? t(`organization_management.roles.${userRole}`, userRole)
                  : ""}
              </span>
            </div>
          </div>
          <div className="flex space-x-3">
            {canManageMembers && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {t("organization_management.invite_member")}
              </button>
            )}
            {canManageSettings && (
              <button
                onClick={() => setShowSettingsModal(true)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <Settings className="w-4 h-4 mr-2" />
                {t("organization_management.settings")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  {t("organization_management.stats.total_members")}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.members?.total}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building2 className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  {t("organization_management.stats.total_projects")}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.projects?.total}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Shield className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  {t("organization_management.stats.admins")}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.members?.admins}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Mail className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  {t("organization_management.stats.pending_invites")}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {invitations?.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {["overview", "members", "invitations"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t(`organization_management.tabs.${tab}`)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t("organization_management.overview.title")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">
                  {t("organization_management.overview.details")}
                </h4>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm text-gray-500">
                      {t("organization_management.overview.name")}
                    </dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {organization?.name}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">
                      {t("organization_management.overview.description")}
                    </dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {organization?.description ||
                        t("organization_management.overview.no_description")}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "members" && (
        <div className="bg-white rounded-lg shadow border">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {t("organization_management.members.title")}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t("organization_management.members.subtitle")}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("organization_management.members.headers.member")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("organization_management.members.headers.role")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("organization_management.members.headers.joined")}
                  </th>
                  {canManageMembers && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("organization_management.members.headers.actions")}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {members.map((member) => (
                  <tr key={member.user._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {member.user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.user.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {member.user.email}
                          </div>
                        </div>
                        {organization?.owner === member.user._id && (
                          <Crown className="w-4 h-4 text-yellow-500 ml-2" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(
                          member.role,
                        )}`}
                      >
                        {t(
                          `organization_management.roles.${member.role}`,
                          member.role,
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(member.joinedAt).toLocaleDateString()}
                    </td>
                    {canManageMembers && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {organization?.owner !== member.user._id && (
                          <div className="flex space-x-2">
                            <select
                              value={member.role}
                              onChange={(e) =>
                                handleUpdateMemberRole(
                                  member.user._id,
                                  e.target.value,
                                )
                              }
                              className="text-xs border rounded px-2 py-1"
                            >
                              <option value="member">
                                {t("organization_management.roles.member")}
                              </option>
                              <option value="admin">
                                {t("organization_management.roles.admin")}
                              </option>
                            </select>
                            <button
                              onClick={() =>
                                handleRemoveMember(member.user._id)
                              }
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "invitations" && (
        <div className="bg-white rounded-lg shadow border">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {t("organization_management.invitations.title")}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t("organization_management.invitations.subtitle")}
            </p>
          </div>
          {invitations?.length === 0 ? (
            <div className="p-6 text-center">
              <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {t("organization_management.invitations.empty")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("organization_management.invitations.headers.email")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("organization_management.invitations.headers.role")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t(
                        "organization_management.invitations.headers.invited_by",
                      )}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("organization_management.invitations.headers.sent")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invitations.map((invitation, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invitation.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(
                            invitation.role,
                          )}`}
                        >
                          {t(
                            `organization_management.roles.${invitation.role}`,
                            invitation.role,
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invitation.invitedBy?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invitation.invitedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("organization_management.invite_modal.title")}
                </h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleInviteUser}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("organization_management.invite_modal.email_label")}
                  </label>
                  <input
                    type="email"
                    required
                    value={inviteForm.email}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, email: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t(
                      "organization_management.invite_modal.email_placeholder",
                    )}
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("organization_management.invite_modal.role_label")}
                  </label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, role: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="member">
                      {t("organization_management.roles.member")}
                    </option>
                    <option value="admin">
                      {t("organization_management.roles.admin")}
                    </option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    {t("organization_management.invite_modal.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {t("organization_management.invite_modal.submit")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("organization_management.settings_modal.title")}
                </h3>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleUpdateSettings}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("organization_management.settings_modal.name_label")}
                    </label>
                    <input
                      type="text"
                      required
                      value={settingsForm.name || ""}
                      onChange={(e) =>
                        setSettingsForm({
                          ...settingsForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t(
                        "organization_management.settings_modal.description_label",
                      )}
                    </label>
                    <textarea
                      value={settingsForm.description || ""}
                      onChange={(e) =>
                        setSettingsForm({
                          ...settingsForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="3"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowSettingsModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    {t("organization_management.settings_modal.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {t("organization_management.settings_modal.submit")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationManagement;
